import {CommonModule} from '@angular/common';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  DoBootstrap,
  ErrorHandler,
  inject,
  Injector,
  NgModule,
} from '@angular/core';
import {createCustomElement} from '@angular/elements';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatRadioModule} from '@angular/material/radio';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouterModule} from '@angular/router';
import {EffectsModule} from '@ngrx/effects';
import {StoreRouterConnectingModule} from '@ngrx/router-store';
import {Store, StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {environment} from '../environments/environment';
import {AddOnModule} from './add-ons/add-on.module';
import {CommerceRoutingModule} from './commerce-routing.module';
import {CommerceComponent} from './commerce.component';
import {CatalogModule} from './common/catalog/catalog.module';
import {CommonComponentsModule} from './common/components/common-components.module';
import {ContextDataModule} from './common/context-data';
import {CurrentUserModule} from './common/current-user';
import {GlobalErrorHandler} from './common/error-handling';
import {ErrorHandlingModule} from './common/error-handling/error-handling.module';
import {AnalyticsService, CoreModule} from './common/service';
import {MuteAnalyticsService} from './common/service/core/event-analytics.service';
import {SportsEngineAnalyticsService} from './common/service/core/sportsengine-analytics.service';
import {CartModule} from './common/shopping-cart/cart.module';
import {CartCompanionComponent} from './common/shopping-cart/components/cart-companion/cart-companion.component';
import {AddressApiService} from './data/api/service';
import {metaReducers, ROOT_REDUCERS} from './reducers';
import {SeFeComponentsModule} from './se-fe-components.module';
import {TaxAndShippingModule} from './tax-and-shipping/tax-and-shipping.module';
import {setOrganizationHeader} from './common/service/core/organization-interceptor';
import {EventEffects} from './common/event-emitter/event.effects';
import {SeFeDomainEnvironmentModule} from 'se-fe-domain-environment';
import {StorageModule} from './data/storage';
import {SeFeFrameRouterModule} from 'se-fe-frame-router';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const win = window as any;

@NgModule({
  declarations: [CommerceComponent],
  imports: [
    AddOnModule,
    BrowserAnimationsModule,
    CartModule,
    CatalogModule,
    CoreModule,
    CommonModule,
    CommonComponentsModule,
    ContextDataModule,
    CurrentUserModule,
    EffectsModule.forRoot([EventEffects]),
    ErrorHandlingModule,
    FormsModule,
    MatRadioModule,
    ReactiveFormsModule,
    RouterModule,
    CommerceRoutingModule,
    SeFeComponentsModule,
    StorageModule,
    StoreModule.forRoot(ROOT_REDUCERS, {metaReducers}),
    StoreRouterConnectingModule.forRoot(),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    TaxAndShippingModule,
    SeFeDomainEnvironmentModule.forRoot(environment),
    SeFeFrameRouterModule.forRoot({
      origin: [''],
      overlaySelector: '.se-fe-modal--open, .se-fe-modal',
      overlayDelay: 300,
    }),
  ],
  providers: [
    AddressApiService,
    {provide: 'seAnalytics', useValue: win.seAnalytics},
    {provide: Window, useValue: window},
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    {
      provide: AnalyticsService,
      deps: [Window, Store],
      useFactory: (window: Window): AnalyticsService => {
        if (
          window.location.hostname.match(
            /(?:sportsengine|sportngin)\.com$|(?:sestage\.us|ngin-staging\.com)$/i,
          )
        ) {
          return inject(SportsEngineAnalyticsService);
        }
        return inject(MuteAnalyticsService);
      },
    },
    provideHttpClient(
      withXhr(),
      withInterceptorsFromDi(),
      withInterceptors([setOrganizationHeader]),
    ),
  ],
})
export class AppModule implements DoBootstrap {
  constructor(private injector: Injector) {}

  public ngDoBootstrap(): void {
    this.bootstrapWebComponents();
  }

  private bootstrapWebComponents(): void {
    const commerceElement = createCustomElement(CommerceComponent, {
      injector: this.injector,
    });
    customElements.define('sales-channels-element', commerceElement);

    const cartCompanionComponent = createCustomElement(CartCompanionComponent, {
      injector: this.injector,
    });
    customElements.define(
      'sales-channels-cart-companion',
      cartCompanionComponent,
    );
  }
}
